import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy } from '@angular/core';
import { Validators, NonNullableFormBuilder } from '@angular/forms';
import { NgbModalRef, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { filter, take, map } from 'rxjs/operators';
import {
  BaseAbsenceTypes,
  BaseAbsenceTypeEnum,
  baseCandidateActions,
  BaseCoreState,
  BaseModalService,
  BaseDateService,
  ProfileAbsenceConflictsListItemResponse,
  ProfileAbsenceListItemResponse,
  ProfileShowResponse,
  ProfileCreateAbsenceRequest,
  baseCandidatesSelectors,
  AbsenceType,
  BaseAbsencePayloadTypeEnum,
} from 'base';
import { AbsencePagination } from '../edit-candidate/components/absence-time/absence-time.component';

@Component({
  selector: 'growth-add-absence',
  templateUrl: './add-absence.component.html',
})
export class AddAbsenceComponent implements OnInit, OnDestroy {
  @ViewChild('absenceConflictsModal', { static: true }) absenceConflictsModal: ElementRef;
  @Input() profile: ProfileShowResponse | undefined;
  @Input() absenceToEdit: ProfileAbsenceListItemResponse;
  @Input() absencePagination: AbsencePagination;
  @Input() inquiryId: number;
  @Input() reloadOffersAndCandidates = false;
  @Input() activeTabId: number | string;

  @Output() cancel: EventEmitter<boolean> = new EventEmitter<boolean>();

  form = this.fb.group({
    range: [null as { from: string; to: string } | null, [Validators.required]],
    type: ['' as AbsenceType, [Validators.required]],
    note: [''],
  });

  validated = false;
  minDate: NgbDateStruct = BaseDateService.getFirstDayOfPreviousMonth();
  conflicts: Array<ProfileAbsenceConflictsListItemResponse> = [];
  modalRef: NgbModalRef;
  types = BaseAbsenceTypes;
  absenceTypes = BaseAbsenceTypeEnum;
  absencePayloadTypes = BaseAbsencePayloadTypeEnum;

  constructor(
    private fb: NonNullableFormBuilder,
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy() {
    this.store.dispatch(baseCandidateActions.resetAbsenceConflicts());
  }

  onSubmit(): void {
    this.validated = true;
    this.form.markAllAsTouched();
    if (!this.form.valid || !this.profile?.id) {
      return;
    }
    this.checkConflictsAndProceed();
  }

  closeModal() {
    this.cancel.emit(true);
  }

  private checkConflictsAndProceed(): void {
    if (!this.profile) {
      return;
    }

    const { from, to } = this.form.getRawValue().range!;

    this.store.dispatch(
      baseCandidateActions.loadAbsenceConflicts({
        payload: { profile: this.profile.id as number, startDate: from, endDate: to },
      })
    );

    this.store
      .select(baseCandidatesSelectors.getAbsenceConflicts)
      .pipe(
        filter((state) => !!state.ok),
        map((state) => state.data as ProfileAbsenceConflictsListItemResponse[]),
        take(1)
      )
      .subscribe((conflicts) => {
        this.conflicts = conflicts;

        if (conflicts.length) {
          this.openConflictsModal();
        } else {
          this.absenceToEdit ? this.updateAbsence() : this.createAbsence();
        }
      });
  }

  private initForm(): void {
    if (this.absenceToEdit) {
      this.form.patchValue({
        range:
          this.absenceToEdit.startDate && this.absenceToEdit.endDate
            ? { from: this.absenceToEdit.startDate, to: this.absenceToEdit.endDate }
            : null,
        type: this.absenceToEdit.type,
        note: this.absenceToEdit.note || '',
      });
    } else {
      const defaultType = this.types[0].value as AbsenceType;
      this.form.patchValue({ type: defaultType });
    }
  }

  private openConflictsModal(): void {
    this.modalRef = this.modalService.open(
      {
        headText: 'work.absences.conflictsModal.title',
        bodyTemplate: this.absenceConflictsModal,
        okText: 'action.confirm',
      },
      { size: 'md' }
    );

    this.modalRef.result.then(
      () => {
        this.absenceToEdit ? this.updateAbsence() : this.createAbsence();
      },
      () => {
        this.validated = false;
      }
    );
  }

  private createAbsence(): void {
    const formValue = this.form.getRawValue();

    const body: ProfileCreateAbsenceRequest = {
      endDate: formValue.range?.to || '',
      startDate: formValue.range?.from || '',
      type: formValue.type,
      note: formValue.note,
    };
    const activeTabId = this.activeTabId.toString() as BaseAbsenceTypeEnum;

    this.store.dispatch(
      baseCandidateActions.createAbsenceAction({
        payload: {
          profile: this.profile?.id as number,
          body: body,
          noLoading: true,
          inquiry: this.reloadOffersAndCandidates ? this.inquiryId : undefined,
          absenceTab: this.activeTabId !== this.absenceTypes.all ? this.absencePayloadTypes[activeTabId] : undefined,
        },
      })
    );
    this.closeModal();
  }

  private updateAbsence() {
    if (!this.profile) {
      return;
    }

    const formValue = this.form.getRawValue();

    const body: ProfileCreateAbsenceRequest = {
      endDate: formValue.range?.to || '',
      startDate: formValue.range?.from || '',
      type: formValue.type,
      note: formValue.note,
    };
    const activeTabId = this.activeTabId.toString() as BaseAbsenceTypeEnum;

    this.store.dispatch(
      baseCandidateActions.updateAbsenceAction({
        payload: {
          absence: this.absenceToEdit.id,
          profile: this.profile.id as number,
          inquiry: this.reloadOffersAndCandidates ? this.inquiryId : undefined,
          body,
          absenceTab: this.activeTabId !== this.absenceTypes.all ? this.absencePayloadTypes[activeTabId] : undefined,
        },
      })
    );
    this.closeModal();
  }
}
